import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { OfertasHorarioService } from '../../../ofertas-horario/Service';
import { useFilterApp } from '../../../../contexts/FilterApp';
import { useParamsContext } from '../../../../contexts/ParamsContext';
import { Modal } from '../../../../components/Modal';
import { OfferDetails } from '../../../ofertas-horario/OfferDetails';
import { formatOfferRange, getRecipientName, getRecipientStatus } from '../../../ofertas-horario/helpers';
import { ChevronDown, ChevronUp, Megaphone } from 'lucide-react';

const ofertasService = new OfertasHorarioService();

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value || 0),
  );

export function OffersDayPanel({ date }: { date: string | Date }) {
  const { attendantId } = useFilterApp();
  const { setParams } = useParamsContext();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<any>(null);
  const [detailsId, setDetailsId] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const dateKey = useMemo(() => moment(date).format('YYYY-MM-DD'), [date]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ofertasService.day(dateKey, attendantId);
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [attendantId, dateKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener('offers:refresh', handler as EventListener);
    return () => window.removeEventListener('offers:refresh', handler as EventListener);
  }, [load]);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [open]);

  const quotaText = data?.quota
    ? `${data.quota.used}/${data.quota.limit}`
    : '';

  const offers = useMemo(
    () => (Array.isArray(data?.offers) ? data.offers : []),
    [data?.offers],
  );

  const totals = useMemo(() => {
    let bookings = 0;
    let revenue = 0;
    let minutes = 0;
    for (const o of offers) {
      bookings += Number(o?.metrics?.bookings || 0);
      revenue += Number(o?.metrics?.recoveredRevenue || 0);
      minutes += Number(o?.metrics?.filledMinutes || 0);
    }
    return { bookings, revenue, minutes };
  }, [offers]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        className="inline-flex h-8 items-center gap-1.5 rounded-full bg-[rgb(118_118_128/0.12)] px-3 text-sm font-medium transition-colors hover:bg-[rgb(118_118_128/0.2)] disabled:opacity-60"
        onClick={() => setOpen((v) => !v)}
        disabled={loading}
        title={data?.quota ? `Histórico de ofertas do dia · ${quotaText} envios hoje` : 'Histórico de ofertas do dia'}
        aria-expanded={open}
      >
        <Megaphone className="text-primary" aria-hidden="true" />
        Ofertas
        {!!quotaText && <span className="text-xs font-normal tabular-nums text-base-content/55">{quotaText}</span>}
        {open ? <ChevronUp className="text-[10px] text-base-content/45" aria-hidden="true" /> : <ChevronDown className="text-[10px] text-base-content/45" aria-hidden="true" />}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-40 mt-2 max-h-[70vh] overflow-y-auto rounded-[1.25rem] border border-base-content/10 bg-base-100 p-4 shadow-xl"
          style={{ width: 'min(440px, calc(100vw - 2rem))' }}
        >
          <div className="flex flex-wrap items-center gap-3 text-xs text-base-content/70">
            <div>
              <span className="font-semibold">Agendamentos:</span> {totals.bookings}
            </div>
            <div>
              <span className="font-semibold">Minutos:</span> {totals.minutes}
            </div>
            <div>
              <span className="font-semibold">Receita:</span> {formatCurrency(totals.revenue)}
            </div>
            <button type="button" className="btn btn-xs btn-ghost ml-auto" onClick={load} disabled={loading}>
              Atualizar
            </button>
          </div>

          {!offers.length && (
            <div className="mt-3 text-sm text-base-content/60">Nenhuma oferta enviada neste dia.</div>
          )}

          {!!offers.length && (
            <div className="mt-3 space-y-2">
              {offers.map((offer: any) => (
                <div key={offer?._id} className="rounded-xl bg-base-100/75 p-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-semibold">
                      {formatOfferRange(offer)}
                    </span>
                    {Number(offer?.discountPercent || 0) > 0 && (
                      <span className="sf-tag" data-tone="green">{offer.discountPercent}%</span>
                    )}
                    <span className="ml-auto text-xs text-base-content/60">
                      {offer?.metrics?.bookings || 0} agend. · {formatCurrency(Number(offer?.metrics?.recoveredRevenue || 0))}
                    </span>
                    <button
                      type="button"
                      className="btn btn-xs btn-ghost"
                      onClick={() => setDetailsId(String(offer?._id || ''))}
                      title="Abrir detalhe da oferta"
                    >
                      Detalhes
                    </button>
                  </div>

                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {(offer?.recipients || []).slice(0, 8).map((r: any) => (
                      <button
                        key={r?._id}
                        type="button"
                        className={`flex items-center justify-between rounded-lg bg-base-200/40 px-2 py-1 text-left ${
                          r?.bookingHorarioId ? 'hover:bg-base-200/60 cursor-pointer' : 'cursor-default'
                        }`}
                        title={
                          r?.bookingHorarioId
                            ? 'Abrir agendamento convertido'
                            : 'Sem agendamento convertido'
                        }
                        onClick={() => {
                          if (!r?.bookingHorarioId) return;
                          setParams({ action: 'view', id: r.bookingHorarioId });
                        }}
                      >
                        <span className="min-w-0">
                          <span className="block text-xs text-base-content/80 truncate">
                            {getRecipientName(r)}
                          </span>
                          {String(r?.client?.phoneNumber || '').trim() && (
                            <span className="block text-[11px] text-base-content/50 truncate">
                              {String(r.client.phoneNumber).trim()}
                            </span>
                          )}
                        </span>
                        <span className="sf-tag ml-2 shrink-0" data-tone={getRecipientStatus(r).tone}>
                          {getRecipientStatus(r).label}
                        </span>
                      </button>
                    ))}
                  </div>

                  {Array.isArray(offer?.recipients) && offer.recipients.length > 8 && (
                    <div className="mt-2 text-xs text-base-content/60">
                      +{offer.recipients.length - 8} destinatários
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!!detailsId && (
        <Modal title="Detalhe da oferta" onClose={() => setDetailsId('')}>
          <OfferDetails
            offerId={detailsId}
            onOpenSchedule={(horarioId) => {
              setDetailsId('');
              setParams({ action: 'view', id: horarioId });
            }}
          />
        </Modal>
      )}
    </div>
  );
}
